import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, Plus, Calendar, Stethoscope, Shield, MessageSquare, Send, FileText, Camera, Paperclip, X, CheckCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const EmployeeLeaves = ({ auth, onLogout }) => {
  const [activeTab, setActiveTab] = useState('demandes');
  const [leaves, setLeaves] = useState([]);
  const [comms, setComms] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showCommDialog, setShowCommDialog] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [formData, setFormData] = useState({ leave_type: 'congé', start_date: '', end_date: '', reason: '' });
  const [commForm, setCommForm] = useState({ comm_type: 'maladie', message: '', visibility: 'all' });
  const [commFile, setCommFile] = useState(null);
  const [commFilePreview, setCommFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [leavesRes, commsRes] = await Promise.all([
        axios.get(`${API}/leaves/employee/${auth.employee.id}`),
        axios.get(`${API}/communications/employee/${auth.employee.id}`),
      ]);
      setLeaves(leavesRes.data);
      setComms(commsRes.data);
    } catch (err) { console.error(err); }
    try {
      const balRes = await axios.get(`${API}/leaves/balance/${auth.employee.id}`);
      setBalance(balRes.data);
    } catch (err) { console.error('Balance error:', err); }
    finally { setLoading(false); }
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    if (!formData.start_date || !formData.end_date || !formData.reason) {
      toast.error('Veuillez remplir tous les champs'); return;
    }
    try {
      await axios.post(`${API}/leaves`, {
        employee_id: auth.employee.id,
        employee_name: `${auth.employee.first_name} ${auth.employee.last_name}`,
        ...formData,
      });
      toast.success('Demande envoyee');
      setShowDialog(false);
      setFormData({ leave_type: 'congé', start_date: '', end_date: '', reason: '' });
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleSubmitComm = async (e) => {
    e.preventDefault();
    if (!commForm.message.trim()) { toast.error('Veuillez ecrire un message'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('employee_id', auth.employee.id);
      fd.append('employee_name', `${auth.employee.first_name} ${auth.employee.last_name}`);
      fd.append('comm_type', commForm.comm_type);
      fd.append('subject', commForm.comm_type.charAt(0).toUpperCase() + commForm.comm_type.slice(1));
      fd.append('message', commForm.message);
      fd.append('visibility', commForm.visibility);
      if (commFile) fd.append('file', commFile);

      await axios.post(`${API}/communications`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Communication envoyee');
      setShowCommDialog(false);
      setCommForm({ comm_type: 'maladie', message: '', visibility: 'all' });
      setCommFile(null);
      setCommFilePreview(null);
      loadData();
    } catch { toast.error('Erreur'); }
    finally { setUploading(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCommFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setCommFilePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setCommFilePreview(null);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedComm) return;
    try {
      await axios.post(`${API}/communications/${selectedComm.id}/reply`, { message: replyText, sender: 'employee' });
      toast.success('Reponse envoyee');
      setReplyText('');
      setSelectedComm(null);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return { label: 'Approuve', cls: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle };
    if (status === 'rejected') return { label: 'Refuse', cls: 'bg-red-500/20 text-red-400', icon: XCircle };
    return { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400', icon: Clock };
  };

  const LEAVE_BADGE = { 'congé': 'bg-purple-500/20 text-purple-300', vacances: 'bg-blue-500/20 text-blue-300', autre: 'bg-slate-500/20 text-slate-300' };
  const COMM_BADGE = { maladie: 'bg-red-500/20 text-red-300', accident: 'bg-orange-500/20 text-orange-300', autre: 'bg-slate-500/20 text-slate-300' };
  const COMM_ICON = { maladie: <Stethoscope className="w-4 h-4 text-red-400" />, accident: <Shield className="w-4 h-4 text-orange-400" />, autre: <MessageSquare className="w-4 h-4 text-slate-400" /> };

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="sticky top-0 z-10 border-b border-white/10 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/employee/dashboard')} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <FileText className="w-5 h-5 text-violet-400" />
        <h1 className="text-white font-semibold">Mes Demandes</h1>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-400 hover:text-white hover:bg-white/10" data-testid="logout-button">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Tabs */}
        <div className="flex bg-slate-800/60 rounded-xl p-1" data-testid="tabs">
          <button onClick={() => setActiveTab('demandes')} className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all ${activeTab === 'demandes' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`} data-testid="tab-demandes">
            <Calendar className="w-4 h-4 inline mr-2" /> Demandes
          </button>
          <button onClick={() => setActiveTab('communication')} className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all ${activeTab === 'communication' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`} data-testid="tab-communication">
            <AlertTriangle className="w-4 h-4 inline mr-2" /> Communication
          </button>
        </div>

        {/* === DEMANDES TAB === */}
        {activeTab === 'demandes' && (
          <>
            {/* Solde conges */}
            {balance && (
              <Card className="p-4 rounded-xl bg-slate-800/70 border-slate-700/50" data-testid="leave-balance-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Solde conges</p>
                      <p className="text-lg font-bold text-white" data-testid="leave-balance-remaining">{balance.days_remaining} <span className="text-sm font-normal text-slate-400">jours restants</span></p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{balance.days_used} utilise{balance.days_used > 1 ? 's' : ''} / {balance.leave_balance} total</p>
                    <div className="w-24 h-1.5 bg-slate-700 rounded-full mt-1.5 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (balance.days_used / Math.max(1, balance.leave_balance)) * 100)}%`, background: balance.days_remaining <= 3 ? '#ef4444' : '#8b5cf6' }} />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="new-leave-button">
                  <Plus className="mr-2 w-5 h-5" /> Nouvelle demande
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouvelle demande</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmitLeave} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Type de demande</Label>
                    <Select value={formData.leave_type} onValueChange={(v) => setFormData({ ...formData, leave_type: v })}>
                      <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="congé">Conge</SelectItem>
                        <SelectItem value="vacances">Vacances</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Date debut</Label>
                      <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required data-testid="leave-start-date" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date fin</Label>
                      <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required data-testid="leave-end-date" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Raison / Motif</Label>
                    <Textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Decrivez votre demande..." rows={3} required data-testid="leave-reason" />
                  </div>
                  <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="submit-leave-button">Envoyer la demande</Button>
                </form>
              </DialogContent>
            </Dialog>

            {loading ? <p className="text-center text-slate-400 py-8">Chargement...</p>
             : leaves.length === 0 ? (
              <div className="text-center py-12"><Calendar className="w-12 h-12 mx-auto mb-3 text-slate-600" /><p className="text-slate-400">Aucune demande</p></div>
            ) : (
              <div className="space-y-3">
                {leaves.map((leave) => {
                  const st = getStatusBadge(leave.status);
                  const StIcon = st.icon;
                  return (
                    <Card key={leave.id} className="p-4 rounded-xl bg-slate-800/70 border-slate-700/50" data-testid={`leave-item-${leave.id}`}>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Calendar className="w-4 h-4 text-blue-400" />
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${LEAVE_BADGE[leave.leave_type] || LEAVE_BADGE.autre}`}>{leave.leave_type}</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md flex items-center gap-1 ${st.cls}`}><StIcon className="w-3 h-3" />{st.label}</span>
                        </div>
                        <p className="text-sm text-slate-200">
                          {leave.start_date && format(new Date(leave.start_date + 'T00:00:00'), 'd MMM', { locale: fr })}
                          {' – '}
                          {leave.end_date && format(new Date(leave.end_date + 'T00:00:00'), 'd MMM yyyy', { locale: fr })}
                        </p>
                        <p className="text-sm text-slate-400">{leave.reason}</p>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* === COMMUNICATION TAB === */}
        {activeTab === 'communication' && (
          <>
            <Dialog open={showCommDialog} onOpenChange={setShowCommDialog}>
              <DialogTrigger asChild>
                <Button className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="new-comm-button">
                  <AlertTriangle className="mr-2 w-5 h-5" /> Nouvelle communication
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Communication</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmitComm} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={commForm.comm_type} onValueChange={(v) => setCommForm({ ...commForm, comm_type: v })}>
                      <SelectTrigger data-testid="comm-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maladie">Maladie</SelectItem>
                        <SelectItem value="accident">Accident</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea value={commForm.message} onChange={(e) => setCommForm({ ...commForm, message: e.target.value })} placeholder="Decrivez la situation..." rows={3} required data-testid="comm-message" />
                  </div>
                  {/* File attachment */}
                  <div className="space-y-2">
                    <Label>Joindre un fichier (certificat medical, etc.)</Label>
                    <div className="flex gap-2">
                      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*,.pdf" className="hidden" />
                      <input type="file" ref={cameraInputRef} onChange={handleFileSelect} accept="image/*" capture="environment" className="hidden" />
                      <Button type="button" variant="outline" size="sm" onClick={() => cameraInputRef.current?.click()} className="flex-1" data-testid="comm-camera-btn">
                        <Camera className="w-4 h-4 mr-1" /> Photo
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="flex-1" data-testid="comm-file-btn">
                        <Paperclip className="w-4 h-4 mr-1" /> Fichier
                      </Button>
                    </div>
                    {commFile && (
                      <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
                        {commFilePreview ? (
                          <img src={commFilePreview} alt="preview" className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <FileText className="w-6 h-6 text-slate-400" />
                        )}
                        <span className="text-xs text-slate-600 truncate flex-1">{commFile.name}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setCommFile(null); setCommFilePreview(null); }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Visibility choice */}
                  <div className="space-y-2">
                    <Label>Visible par</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={commForm.visibility === 'all' ? 'default' : 'outline'}
                        size="sm"
                        className={`flex-1 ${commForm.visibility === 'all' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                        onClick={() => setCommForm({ ...commForm, visibility: 'all' })}
                        data-testid="comm-visibility-all"
                      >
                        Tout le monde
                      </Button>
                      <Button
                        type="button"
                        variant={commForm.visibility === 'admin_only' ? 'default' : 'outline'}
                        size="sm"
                        className={`flex-1 ${commForm.visibility === 'admin_only' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                        onClick={() => setCommForm({ ...commForm, visibility: 'admin_only' })}
                        data-testid="comm-visibility-admin"
                      >
                        Admin seulement
                      </Button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700 text-white" disabled={uploading} data-testid="submit-comm-button">
                    <Send className="mr-2 w-4 h-4" /> {uploading ? 'Envoi...' : 'Envoyer'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {loading ? <p className="text-center text-slate-400 py-8">Chargement...</p>
             : comms.length === 0 ? (
              <div className="text-center py-12"><MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-600" /><p className="text-slate-400">Aucune communication</p></div>
            ) : (
              <div className="space-y-3">
                {comms.map((comm) => (
                  <Card key={comm.id} className="p-4 rounded-xl bg-slate-800/70 border-slate-700/50" data-testid={`comm-item-${comm.id}`}>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {COMM_ICON[comm.comm_type] || COMM_ICON.autre}
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${COMM_BADGE[comm.comm_type] || COMM_BADGE.autre}`}>
                            {comm.comm_type || comm.subject}
                          </span>
                          {comm.visibility === 'admin_only' && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-amber-500/20 text-amber-400">Admin</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">{comm.created_at && format(new Date(comm.created_at), 'd MMM HH:mm', { locale: fr })}</span>
                      </div>
                      <p className="text-sm text-slate-300 bg-slate-700/50 p-3 rounded-lg">{comm.message}</p>
                      {comm.file_url && (
                        <a href={comm.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300">
                          <Paperclip className="w-3.5 h-3.5" /> Piece jointe
                        </a>
                      )}
                      {comm.replies?.length > 0 && (
                        <div className="space-y-2 pl-3 border-l-2 border-slate-600">
                          {comm.replies.map((r, i) => (
                            <div key={i} className={`p-3 rounded-lg text-sm ${r.sender === 'admin' ? 'bg-blue-500/10 text-blue-200' : 'bg-slate-700/50 text-slate-300'}`}>
                              <span className={`text-xs font-bold ${r.sender === 'admin' ? 'text-blue-400' : 'text-slate-400'}`}>{r.sender === 'admin' ? 'Administration' : 'Vous'}</span>
                              <p className="mt-1">{r.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedComm?.id === comm.id ? (
                        <div className="flex gap-2">
                          <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre reponse..." className="flex-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid={`reply-input-${comm.id}`} />
                          <Button size="sm" onClick={handleReply} className="bg-blue-600 hover:bg-blue-700" data-testid={`send-reply-${comm.id}`}><Send className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10" onClick={() => setSelectedComm(comm)} data-testid={`reply-btn-${comm.id}`}>Repondre</Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeLeaves;
